import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Camera,
  Sparkles,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Send,
  Building2,
  Sliders,
  Tv,
  Monitor,
  Scissors,
  Wand2,
  Eye,
  TrendingUp,
  Image as ImageIcon,
  Sun,
  Layers,
  ShoppingBag,
} from "lucide-react";

export const Route = createFileRoute("/services_/photography")({
  head: () => ({
    meta: [
      {
        title:
          "Corporate & Product Photography Services — KMWAdvertising LLP",
      },
      {
        name: "description",
        content:
          "Professional Corporate & Product Photography Services by KMWAdvertising LLP. High-quality imagery for eCommerce, catalogues, lifestyle, industrial, and 360° product imaging.",
      },
      {
        property: "og:title",
        content: "Corporate & Product Photography Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Professional photography that builds trust, elevates brand perception, and increases online conversion rates across all customer touchpoints.",
      },
    ],
  }),
  component: PhotographyServicePage,
});

export function PhotographyServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const productServices = [
    "eCommerce Product Photography",
    "Catalogue Photography",
    "White Background Photography",
    "Lifestyle Product Photography",
    "Fashion Photography",
    "Jewellery Photography",
    "Food Photography",
    "FMCG Photography",
    "Packaging Photography",
    "Industrial Product Photography",
    "Automotive Photography",
    "360-Degree Product Imaging",
  ];

  const postProductionServices = [
    "Colour Correction",
    "Retouching",
    "Background Removal",
    "Shadow Enhancement",
    "Reflection Creation",
    "Image Optimisation",
    "Web Compression",
    "Print Optimisation",
    "AI Image Enhancement",
    "Batch Editing",
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-rose-600/15 to-purple-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">Photography</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Camera className="h-3.5 w-3.5" />
              Corporate & Product Photography
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Photography
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                Professional Photography That Builds Trust and Elevates Your Brand
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <p>
                Photography is often the first visual impression customers have of your business. High-quality imagery enhances credibility, improves marketing performance and creates a stronger emotional connection with your audience.
              </p>
              <p className="text-sm sm:text-base text-pink-300/90 font-medium">
                At KMWAdvertising LLP, we provide professional photography services designed to support websites, advertising campaigns, social media, brochures, annual reports and eCommerce platforms.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#photo-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Book Photography Shoot</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#product-photography"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore Photography Services</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Product Photography */}
      <section id="product-photography" className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <ShoppingBag className="h-3.5 w-3.5" />
              High-Conversion Studio Shoots
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Product Photography
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Exceptional product photography increases customer confidence and improves online conversions. Our product photography services include:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {productServices.map((service) => (
              <div
                key={service}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition flex items-center gap-3"
              >
                <div className="h-7 w-7 rounded-lg bg-pink-500/10 border border-pink-500/20 flex items-center justify-center text-pink-400 shrink-0">
                  <Check className="h-3.5 w-3.5" />
                </div>
                <span className="text-xs sm:text-sm font-semibold text-white">{service}</span>
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-pink-300/90 italic font-semibold">
            Every photograph is professionally lit, colour-corrected and retouched to ensure consistency across digital and print applications.
          </p>
        </div>
      </section>

      {/* Photography Post-Production */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <Wand2 className="h-3.5 w-3.5" />
              High-End Retouching & Editing
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Photography Post-Production
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Our editing services include:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-10">
            {postProductionServices.map((service) => (
              <div
                key={service}
                className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center text-xs font-bold text-slate-200 hover:border-purple-500/40 transition"
              >
                {service}
              </div>
            ))}
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-r from-pink-950/30 via-rose-950/20 to-purple-950/30 border border-white/15">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed font-medium">
              Professionally edited images ensure your brand maintains a premium appearance across every customer touchpoint.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="photo-form" className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Photography Shoot Brief
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Book a Commercial Photography Shoot
              </h2>
              <p className="text-slate-300 text-sm">
                Discuss your product catalog, executive portraits, or lifestyle shoot with our studio producers.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Photography Inquiry Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our photography coordinator will reach out within 24 hours to review your shoot requirements.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Meera Kapoor"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Aura Luxury"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="meera@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Shoot Category Focus</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="ecommerce">eCommerce & Catalogue Product Photography</option>
                    <option value="lifestyle">Lifestyle & Fashion Photography</option>
                    <option value="industrial">Industrial & Automotive Product Photography</option>
                    <option value="360">360-Degree Product Imaging & Retouching</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Shoot Details / SKUs Count</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about the number of products, studio preference, or turnaround timeline..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Request Photography Shoot Quote</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
